import { useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { challengeSubmissionApprove, challengeSubmissionDetails, challengeSubmissionReject, submitChallengeVerification, compareFaces } from "../../api/challenge";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { ActionButton } from "../../components/Forms/ActionButton";
import Modal from "./components/Modal";
import { FaAngleDown, FaAngleRight } from "react-icons/fa";
import AllocatePoints from "./components/AllocatePoints";
import InputField from "../../components/Forms/InputField";
import RichTextEditor from "../../components/Forms/RichTextEditor";
import { useGetAppDisplayConfig } from "../../queries/homepageOrganizer";
import Chip from "../../components/Chip";
import FaceComparisonResult from "../../components/FaceVerification/FaceComparisonResult";
import AiReviewPanel, { AiAnswerNote } from "../../components/AiReview/AiReviewPanel";

// The user's answer is the id of the option they picked; show that option's label ('-' if none).
const selectedOptionLabel = (options, answer) => {
    if (!answer) return '-';
    const picked = (options || []).find((o) => String(o._id) === String(answer));
    return picked ? picked.value : String(answer);
};

const SubmissionDetails = () => {

    const { state } = useLocation();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [data, setData] = useState({}); // user-data
    const [submissionData, setSubmissionData] = useState({});
    const [challengeData, setChallengeData] = useState({});
    const [selectedTab, setSelectedTab] = useState(0);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isUserModalOpen, setIsUserModalOpen] = useState(false);
    const [isFinalModalOpen, setIsFinalModalOpen] = useState(false);
    const [expandedQuestionIndex, setExpandedQuestionIndex] = useState({ 0: 0 });
    const [allocatedPoints, setAllocatedPoints] = useState({});
    const [allocatedChallengePoints, setAllocatedChallengePoints] = useState(submissionData?.AllocatePoints || 0);
    const [remarks, setRemarks] = useState({});
    const [error, setError] = useState(null);
    const [disabledControl, setDisabledControl] = useState(submissionData?.reviewStatus === "pending" ? false : true);
    const [carbonEmissionCount, setCarbonEmissionCount] = useState(submissionData?.carbonEmissionCount || 0);
    const [carbonEmissionInfo, setCarbonEmissionInfo] = useState(submissionData?.carbonEmissionInfo || '');
    const [submissionDecision, setSubmissionDecision] = useState('yes');
    const [rejectionReason, setRejectionReason] = useState('');
    const [rejectionError, setRejectionError] = useState('');
    const [showCarbonPointsCalculation, setShowCarbonPointsCalculation] = useState(false);
    
    // Face verification states
    const [faceVerificationResults, setFaceVerificationResults] = useState({});
    const [faceVerificationLoading, setFaceVerificationLoading] = useState({});
    const [faceVerificationErrors, setFaceVerificationErrors] = useState({});

    const [remarksErr, setRemarksErr] = useState('');
    // const [allocatedBonusPoints, setAllocatedBonusPoints] = useState({});
    const [bonusPoints, setBonusPoints] = useState(submissionData?.allocatedBonusPoints || 0);

    // To initialize the allocated points
    useEffect(() => {
        if (submissionData && submissionData.pagesAndQuestions) {
            const points = submissionData.pagesAndQuestions.reduce((acc, page, pageIndex) => {
                page.questions.forEach((question, index) => {
                    if (question.isRejected || question.isApproved) {
                        acc[`${page.pageId}-${index}`] = question.allocatedPoints;
                    }
                    else {
                        acc[`${page.pageId}-${index}`] = challengeData.pagesAndQuestions[pageIndex].questions[index].points;
                    }
                });
                return acc;
            }, {});
            setAllocatedPoints(points);
            setCarbonEmissionCount(submissionData?.carbonEmissionCount);
            setCarbonEmissionInfo(submissionData?.carbonEmissionInfo);
            setRejectionReason(submissionData?.rejectionReason);
            // if (submissionData.reviewStatus === "submitted") {
            //     const btnControl = submissionData.pagesAndQuestions.reduce((acc, page, pageIndex) => {
            //         page.questions.forEach((question, index) => {
            //             if (question.isRejected || question.isApproved) {
            //                 acc[`${page.pageId}-${index}`] = true;
            //             }
            //             else {
            //                 acc[`${page.pageId}-${index}`] = false;
            //             }
            //         });
            //         return acc;
            //     }, {});
            //     setDisabledControl(btnControl);
            //     console.log(btnControl);
            // }
        }
    }, [submissionData]);

    const handleApprove = (submissionId, allocatedPoints, remarks) => {
        challengeSubmissionApprove({
            submissionId,
            userChallengeSubmissionId: state._id,
            allocatedPoints,
            remarks: remarks || ''
        }, setSubmissionData, toast, setLoading);
    }

    const handleReject = (submissionId, page, pageIndex, questionIndex) => {
        const remark = remarks[`${page}-${questionIndex}`];
        // console.log(remark);
        if (!remark) {
            setRemarksErr(true);
            return;
        }
        setAllocatedPoints(pageIndex, questionIndex, 0);
        challengeSubmissionReject({
            submissionId,
            userChallengeSubmissionId: state._id,
            remarks: remark,
            allocatedPoints: 0
        }, setSubmissionData, toast, setLoading);
        console.log(submissionData);
    }

    const handleSubmit = () => {
        // setBonusPoints(submissionData.allocatedBonusPoints === 0 ? challengeData.bonusPoints : submissionData.allocatedBonusPoints);
        setIsFinalModalOpen(true);
    }

    // Calculate suggested points based on confidence
    const calculateSuggestedPoints = (confidence, maxPoints) => {
        if (confidence >= 90) return maxPoints;
        if (confidence >= 80) return Math.round(maxPoints * 0.9);
        if (confidence >= 70) return Math.round(maxPoints * 0.8);
        if (confidence >= 60) return Math.round(maxPoints * 0.6);
        if (confidence >= 50) return Math.round(maxPoints * 0.4);
        return 0;
    };

    // Face verification function
    const triggerFaceVerification = async (pageIndex, questionIndex, pageId) => {
        const key = `${pageId}-${questionIndex}`;
        
        // Check conditions
        const question = challengeData?.pagesAndQuestions[pageIndex]?.questions[questionIndex];
        const submissionQuestion = submissionData?.pagesAndQuestions[pageIndex]?.questions[questionIndex];
        
        console.log('🔍 Face verification trigger called:', {
            pageIndex,
            questionIndex,
            pageId,
            key,
            question: {
                questionType: question?.questionType,
                mediaType: question?.mediaType,
                selfImageRequired: question?.selfImageRequired
            },
            submissionAnswer: submissionQuestion?.answer,
            userProfilePhoto: data.user?.profilePhoto || data.user?.avatar || data.user?.profilePicture
        });
        
        if (
            question?.questionType === 'media' && 
            question?.mediaType === 'image' && 
            question?.selfImageRequired && 
            submissionQuestion?.answer
        ) {
            console.log('✅ All conditions met, starting face verification...');
            setFaceVerificationLoading(prev => ({ ...prev, [key]: true }));
            
            try {
                // SOURCE IMAGE: User's profile photo from data variable
                const sourceImageUrl = data.user.profilepic || data.user.profilePhoto || data.user.avatar || data.user.profilePicture;
                console.log('🔍 Profile photo search result:', {
                    profilepic: data.user.profilepic,
                    found: !!sourceImageUrl,
                    finalUrl: sourceImageUrl
                });
                
                // TARGET IMAGE: User's submitted challenge photo
                const targetImageUrl = submissionQuestion.answer;
                
                if (!sourceImageUrl) {
                    console.warn('⚠️ Face verification skipped: User profile photo not available');
                    setFaceVerificationResults(prev => ({
                        ...prev,
                        [key]: {
                            success: false,
                            match: false,
                            confidence: 0,
                            message: 'Face verification skipped: User profile photo not available',
                            status: 'warning'
                        }
                    }));
                    setFaceVerificationLoading(prev => ({ ...prev, [key]: false }));
                    return;
                }
                
                console.log('Starting face verification:', {
                    userProfile: sourceImageUrl,
                    submission: targetImageUrl
                });
                
                // Call face comparison API
                const result = await compareFaces(sourceImageUrl, targetImageUrl);
                
                // Check if the result indicates an error (API returns error status instead of throwing)
                if (!result.success || result.status === 'warning' || result.error) {
                    // Handle different error formats
                    let errorMessage = 'Face verification failed';
                    if (result.message) {
                        errorMessage = result.message;
                    } else if (result.error) {
                        if (typeof result.error === 'string') {
                            errorMessage = result.error;
                        } else if (typeof result.error === 'object') {
                            // Handle error object formats like {status: 400, message: "..."}
                            errorMessage = result.error.message || 
                                          `Face verification error (${result.error.status || 'Unknown'})` ||
                                          'Face verification service error';
                        } else {
                            errorMessage = 'Face verification service error';
                        }
                    }
                    
                    console.warn('⚠️ Face verification returned error/warning:', {
                        success: result.success,
                        status: result.status,
                        error: result.error,
                        message: result.message,
                        finalErrorMessage: errorMessage
                    });
                    
                    setFaceVerificationResults(prev => ({
                        ...prev,
                        [key]: {
                            ...result,
                            error: errorMessage,
                            sourceImageUrl,
                            targetImageUrl
                        }
                    }));
                    
                    setFaceVerificationErrors(prev => ({
                        ...prev,
                        [key]: errorMessage
                    }));
                } else {
                    // Success case
                    // Calculate suggested points based on confidence
                    const maxPoints = parseInt(question.points);
                    const suggestedPoints = calculateSuggestedPoints(result.confidence, maxPoints);
                    
                    setFaceVerificationResults(prev => ({
                        ...prev,
                        [key]: {
                            ...result,
                            sourceImageUrl,
                            targetImageUrl,
                            suggestedPoints
                        }
                    }));
                    
                    // Auto-populate points
                    setAllocatedPoints(prev => ({
                        ...prev,
                        [key]: suggestedPoints
                    }));
                    
                    // Clear any previous errors
                    setFaceVerificationErrors(prev => ({
                        ...prev,
                        [key]: null
                    }));
                }
                
            } catch (error) {
                console.error('Face verification failed:', error);
                
                // Determine error type for better user experience
                let errorMessage = 'Face verification failed';
                let errorStatus = 'error';
                
                if (error.message.includes('profile photo not available')) {
                    errorMessage = 'Face verification skipped: User profile photo not available';
                    errorStatus = 'warning';
                } else if (error.message.includes('image not found') || error.message.includes('fetch')) {
                    errorMessage = 'Face verification failed: Unable to load images';
                } else if (error.message.includes('Face comparison failed')) {
                    errorMessage = 'Face verification service temporarily unavailable';
                } else if (error.response?.status === 404) {
                    errorMessage = 'Face verification service not available';
                } else if (error.response?.data?.message) {
                    errorMessage = `Face verification failed: ${error.response.data.message}`;
                } else {
                    errorMessage = `Face verification failed: ${error.message || 'Unknown error occurred'}`;
                }
                
                // Store error in both results and errors state
                const errorResult = { 
                    success: false,
                    match: false,
                    confidence: 0,
                    error: errorMessage,
                    status: errorStatus,
                    sourceImageUrl: data.user.profilepic || data.user.profilePhoto || data.user.avatar || data.user.profilePicture,
                    targetImageUrl: submissionQuestion.answer
                };
                
                setFaceVerificationResults(prev => ({
                    ...prev,
                    [key]: errorResult
                }));
                
                setFaceVerificationErrors(prev => ({
                    ...prev,
                    [key]: errorMessage
                }));
            }
            
            setFaceVerificationLoading(prev => ({ ...prev, [key]: false }));
        } else {
            console.log('❌ All conditions NOT met, skipping face verification:', {
                hasQuestionType: question?.questionType === 'media',
                hasMediaType: question?.mediaType === 'image', 
                hasSelfImageRequired: question?.selfImageRequired === true,
                hasAnswer: !!submissionQuestion?.answer,
                actualQuestionType: question?.questionType,
                actualMediaType: question?.mediaType,
                actualSelfImageRequired: question?.selfImageRequired,
                actualAnswer: submissionQuestion?.answer
            });
        }
    };

    const toggleQuestion = async (pageIndex, questionIndex) => {
        console.log('🔄 toggleQuestion called:', { pageIndex, questionIndex });
        
        const newExpandedState = {
            ...expandedQuestionIndex,
            [pageIndex]: expandedQuestionIndex[pageIndex] === questionIndex ? null : questionIndex
        };
        setExpandedQuestionIndex(newExpandedState);
        
        console.log('📊 Expanded state:', { 
            old: expandedQuestionIndex, 
            new: newExpandedState,
            isExpanding: newExpandedState[pageIndex] === questionIndex
        });
        
        // Trigger face verification when accordion opens
        if (newExpandedState[pageIndex] === questionIndex) {
            const page = submissionData?.pagesAndQuestions[pageIndex];
            console.log('🎯 Attempting to trigger face verification...', { page: page?.pageId });
            if (page?.pageId) {
                await triggerFaceVerification(pageIndex, questionIndex, page.pageId);
            } else {
                console.log('❌ No pageId found in page:', page);
            }
        }
    }

    const calculateTotalAllocatedPoints = () => {
        let cp = 0;
        let bp = 0;
        if (!submissionData?.pagesAndQuestions || submissionData?.pagesAndQuestions?.length === 0) return;
        submissionData?.pagesAndQuestions?.forEach((page, pageIndex) => {
            if (page?.questions?.length === 0) return;
            page?.questions.forEach((question, questionIndex) => {
                if (question.isApproved || question.isRejected || question?.isEmpty) {
                    if (page.isBonusPage) {
                        bp += parseInt(question.allocatedPoints) || 0;
                    } else {
                        cp += parseInt(question.allocatedPoints) || 0;
                    }
                }
            });
        });
        submissionData.allocatedPoints = cp;
        submissionData.allocatedBonusPoints = bp;
        setAllocatedChallengePoints(cp);
        setBonusPoints(bp);
    };

    const formatFileSize = (size) => {
        if (size < 1024) return `${size} bytes`;
        else if (size < 1024 * 1024) return `${(size / 1024).toFixed(2)} KB`;
        else return `${(size / (1024 * 1024)).toFixed(2)} MB`;
    };

    const renderUserAnswer = (pageIndex, questionIndex) => {
        // console.log(submissionData?.pagesAndQuestions[pageIndex]?.questions[questionIndex]?.mediaMetaData?.exif?.DateTime);
        if (challengeData?.pagesAndQuestions[pageIndex]?.questions[questionIndex]?.questionType === "media") {
            if (challengeData?.pagesAndQuestions[pageIndex]?.questions[questionIndex]?.mediaType === "video") {
                return (
                    <div className="flex flex-col items-start gap-2">
                        <p className="font-semibold">Media Uploaded By User:</p>
                        {
                            submissionData?.pagesAndQuestions[pageIndex]?.questions[questionIndex]?.answer ?
                                <div className="w-full">
                                    <video src={submissionData?.pagesAndQuestions[pageIndex]?.questions[questionIndex]?.answer} controls className="h-full max-h-96" />
                                    <div className="w-full flex items-center justify-between">
                                        {/* <p className="mt-2 font-semibold">Media Size: {formatFileSize(submissionData?.pagesAndQuestions[pageIndex]?.questions[questionIndex]?.mediaMetaData?.fileSize)}</p> */}
                                        {/* <p className="mt-2 font-semibold">Time: {new Date(submissionData?.pagesAndQuestions[pageIndex]?.questions[questionIndex]?.mediaMetaData?.exif?.DateTime).toLocaleString()}</p> */}
                                        <p className="mt-2 font-semibold">Video Taken Time: {submissionData?.pagesAndQuestions[pageIndex]?.questions[questionIndex]?.mediaMetaData?.exif?.DateTime}</p>
                                    </div>
                                </div>
                                // <img src={submissionData?.pagesAndQuestions[pageIndex]?.questions[questionIndex]?.answer} alt="user submission answer" />
                                : <p>No Media Uploaded!</p>
                        }
                    </div>
                );
            }
            if (challengeData?.pagesAndQuestions[pageIndex]?.questions[questionIndex]?.mediaType === "image") {
                const exifExifData = submissionData?.pagesAndQuestions[pageIndex]?.questions[questionIndex]?.mediaMetaData?.exif["{Exif}"];
                const exifData = submissionData?.pagesAndQuestions[pageIndex]?.questions[questionIndex]?.mediaMetaData?.exif?.DateTime;
                return (
                    <div className="flex flex-col items-start gap-2">
                        <p className="font-semibold">Media Uploaded By User:</p>
                        {
                            submissionData?.pagesAndQuestions[pageIndex]?.questions[questionIndex]?.answer ?
                                <div className="w-full">
                                    <img src={submissionData?.pagesAndQuestions[pageIndex]?.questions[questionIndex]?.answer} alt="user submission answer" className="h-full max-h-96" />
                                    <div className="w-full flex items-center justify-between">
                                        {/* <p className="mt-2 font-semibold">Media Size: {formatFileSize(submissionData?.pagesAndQuestions[pageIndex]?.questions[questionIndex]?.mediaMetaData?.fileSize)}</p> */}
                                        {/* <p className="mt-2 font-semibold">Time: {new Date(submissionData?.pagesAndQuestions[pageIndex]?.questions[questionIndex]?.mediaMetaData?.exif?.DateTime).toLocaleString()}</p> */}
                                        {/* <p className="mt-2 font-semibold">Image Taken Time: {submissionData?.pagesAndQuestions[pageIndex]?.questions[questionIndex]?.mediaMetaData?.exif?.Exif?.DateTimeOriginal}</p> */}
                                        <p className="mt-2 font-semibold">Image Taken Time: {exifExifData?.DateTimeOriginal || exifData || "-"}</p>
                                    </div>
                                </div>
                                : <p>No Media Uploaded!</p>
                        }
                    </div>
                );
            }

        }
        else if (challengeData?.pagesAndQuestions[pageIndex]?.questions[questionIndex]?.questionType === "date") {
            return (
                <div>
                    <div className="flex items-center gap-2">
                        <p className="font-semibold">Date Entered By User:</p>
                        <p className="font-normal">{submissionData?.pagesAndQuestions[pageIndex]?.questions[questionIndex]?.answer?.toRegularDate() ?? '-'}</p>
                    </div>
                    <div className="flex items-center gap-2">
                        <p className="font-semibold">Correct Date:</p>
                        <p className="font-normal">{challengeData?.pagesAndQuestions[pageIndex]?.questions[questionIndex]?.correctDate?.toRegularDate() ?? '-'}</p>
                    </div>
                </div>
            );
        }
        else if (challengeData?.pagesAndQuestions[pageIndex]?.questions[questionIndex]?.questionType === "description") {
            return (
                <div className="flex items-center gap-2">
                    <p className="font-semibold">Answer By User:</p>
                    <p className="font-normal">{submissionData?.pagesAndQuestions[pageIndex]?.questions[questionIndex]?.answer}</p>
                </div>
            );
        }
        else if (challengeData?.pagesAndQuestions[pageIndex]?.questions[questionIndex]?.questionType === "dropdown") {
            return (
                <div>
                    <div className="flex items-center gap-2">
                        <p className="font-semibold">Answer By User:</p>
                        <p className="font-normal">{selectedOptionLabel(challengeData?.pagesAndQuestions[pageIndex]?.questions[questionIndex]?.dropdownValues, submissionData?.pagesAndQuestions[pageIndex]?.questions[questionIndex]?.answer)}</p>
                    </div>
                    <div className="flex items-center gap-2">
                        <p className="font-semibold">Correct Answer:</p>
                        <p className="font-normal">{challengeData?.pagesAndQuestions[pageIndex]?.questions[questionIndex]?.dropdownValues.filter(value => value.correctAnswer ? value.value : "")[0]?.value || '-'}</p>
                    </div>
                </div>
            );
        }
        else if (challengeData?.pagesAndQuestions[pageIndex]?.questions[questionIndex]?.questionType === "mcq") {
            // console.log(challengeData?.pagesAndQuestions[pageIndex]?.questions[questionIndex]?.mcqOptions.filter(value => value.value = submissionData?.pagesAndQuestions[pageIndex]?.questions[questionIndex]?.answer ? value.value : "")[0]?.value || '-');
            return (
                <div>
                    <div className="flex items-center gap-2">
                        <p className="font-semibold">Answer By User:</p>
                        <p className="font-normal">{selectedOptionLabel(challengeData?.pagesAndQuestions[pageIndex]?.questions[questionIndex]?.mcqOptions, submissionData?.pagesAndQuestions[pageIndex]?.questions[questionIndex]?.answer)}</p>
                    </div>
                    <div className="flex items-center gap-2">
                        <p className="font-semibold">Correct Answer:</p>
                        <p className="font-normal">
                            {
                                challengeData?.pagesAndQuestions[pageIndex]?.questions[questionIndex]?.mcqOptions.filter(value => value.correctAnswer ? value.value : "").length > 0
                                    ? challengeData?.pagesAndQuestions[pageIndex]?.questions[questionIndex]?.mcqOptions.filter(value => value.correctAnswer ? value.value : "")[0].value
                                    : 'No Correct Answer Provided'
                            }
                        </p>
                    </div>
                </div>
            );
        }
    }

    const { data: homePageConfig } = useGetAppDisplayConfig();
    
    useEffect(() => {
        console.log('🔍 SubmissionDetails useEffect - checking state:', { state, submissionId: state?._id });
        console.log('🔍 Full location state:', state);
        console.log('🔍 Current URL:', window.location.href);
        
        // Check for authentication token
        const token = localStorage.getItem('cc-t') || sessionStorage.getItem('cc-t');
        console.log('🔍 Auth token present:', !!token);
        if (!token) {
            console.error('❌ No authentication token found');
            setError('Authentication required. Please log in again.');
            setLoading(false);
            toast.error('Authentication required. Please log in again.');
            return;
        }
        
        if (!state || !state._id) {
            console.error('❌ No submission ID found in navigation state');
            console.error('❌ Expected state format: { _id: "submissionId" }');
            setError('Submission ID not found. Please navigate back and try again.');
            setLoading(false);
            toast.error('Submission ID not found. Please navigate back and try again.');
            return;
        }
        
        console.log('✅ Loading submission details for ID:', state._id);
        console.log('✅ Calling challengeSubmissionDetails API...');
        
        challengeSubmissionDetails(
            state._id, 
            setData, 
            setSubmissionData, 
            setChallengeData, 
            setDisabledControl, 
            setSelectedTab, 
            toast, 
            setLoading, 
            setError
        );
    }, [state]);

    // Face verification for already-expanded questions
    useEffect(() => {
        if (challengeData && submissionData && data && !loading) {
            console.log('🔍 Checking for auto face verification on data load...');
            console.log('📊 Data available:', {
                challengeData: !!challengeData,
                submissionData: !!submissionData, 
                userData: !!data,
                expandedQuestionIndex
            });
            
            // Check each expanded question for face verification
            Object.keys(expandedQuestionIndex).forEach(pageIndex => {
                const questionIndex = expandedQuestionIndex[pageIndex];
                if (questionIndex !== null && questionIndex !== undefined) {
                    const page = submissionData?.pagesAndQuestions?.[pageIndex];
                    if (page?.pageId) {
                        console.log(`🎯 Auto-triggering face verification for expanded question ${pageIndex}-${questionIndex}`);
                        triggerFaceVerification(parseInt(pageIndex), questionIndex, page.pageId);
                    }
                }
            });
        }
    }, [challengeData, submissionData, data, loading, expandedQuestionIndex]);

    useEffect(() => {
        // if (challengeData && challengeData.pagesAndQuestions && challengeData.pagesAndQuestions.length > 0) {
        //     setSelectedTab(challengeData.pagesAndQuestions[0].pageId);
        // }
        if (submissionData && submissionData.pagesAndQuestions && submissionData.pagesAndQuestions) {
            const initialRemarks = submissionData.pagesAndQuestions.reduce((acc, page) => {
                page.questions.forEach((question, index) => {
                    acc[`${page.pageId}-${index}`] = question.remarks || '';
                });
                return acc;
            }, {});
            setRemarks(initialRemarks);
        }
        calculateTotalAllocatedPoints();
    }, [submissionData]);

    if (error) {
        return (
            <div className="w-full h-96 flex items-center justify-center">
                <h1 className="text-xl text-red font-bold">{error}</h1>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="w-full h-96 flex items-center justify-center">
                <div className="text-center">
                    <h1 className="text-xl text-gray-400 mb-4">Loading...</h1>
                    <p className="text-sm text-gray-500">
                        Submission ID: {state?._id || 'Not found'}
                    </p>
                    <p className="text-sm text-gray-500">
                        Check browser console for detailed logs
                    </p>
                    {/* Debug section for testing */}
                    {!state?._id && (
                        <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded">
                            <p className="text-red-600 font-medium">Debug: No submission ID in navigation state</p>
                            <button 
                                onClick={() => navigate('/submissions/challenge-submission-details', { 
                                    state: { _id: '680e770c0dbf8d5580f234a3' } 
                                })}
                                className="mt-2 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                            >
                                Test with valid ID
                            </button>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col text-base font-semibold text-gray-900 rounded mr-0">
            <div className="bg-white rounded-md shadow-[0px_0px_25px_rgba(94,92,154,0.1)] max-md:max-w-full p-3">
                <div className="flex justify-between items-center">
                    <h1
                        className="gap-2.5 w-full font-bold max-md:max-w-full text-primary capitalize cursor-pointer"
                        onClick={() => setIsModalOpen(true)}
                    >
                        <span className="text-black">Submitted Challenge:</span> <span title="Challenge Category">{data.challenge.category.name}</span> | <span title="Challenge Name">{data.challenge.name}</span> | <span title="Challenge Complexity">{data.challenge.complexity}</span> | {data.challenge?.inclusive ? "Inclusive Challenge" : "Not a Inclusive Challenge"}
                    </h1>
                    <div className="flex gap-2 items-center">
                        <p className="inline-flex">Status:</p>
                        <Chip value={submissionData?.reviewStatus} color={submissionData?.reviewStatus === 'accepted' ? 'green' : 'red'} />
                        {
                            submissionData?.reviewStatus !== 'pending' &&
                            <div className='flex gap-2 basis-1/12'>
                                <p
                                    className={`font-bold px-4 py-1 rounded cursor-pointer ${disabledControl ? 'bg-primary' : 'bg-navBtn'} ${disabledControl ? 'text-white' : 'text-secondary'}`}
                                    onClick={() => {
                                        setDisabledControl(!disabledControl);
                                    }}
                                >Edit</p>
                            </div>
                        }
                    </div>
                </div>
                <div className="mt-5 flex flex-wrap gap-5">
                    <div className="flex gap-2 basis-1/5">
                        <p className="font-semibold ">Submitted By:</p>
                        <p className="capitalize cursor-pointer text-primary font-bold" onClick={() => setIsUserModalOpen(true)}>{data.user.username}</p>
                    </div>
                    <div className="flex gap-2 basis-1/3">
                        <p className="font-semibold ">Submitted On:</p>
                        <p className="text-primary font-bold">
                            {new Intl.DateTimeFormat("en-IN", {
                                day: "2-digit",
                                month: "2-digit",
                                year: "numeric",
                                hour: "2-digit",
                                minute: "2-digit",
                                weekday: "short",
                            }).format(submissionData?.createdAt && new Date(submissionData?.createdAt))}
                        </p>
                    </div>
                </div>
                {
                    submissionData?.isInvited?._id && (
                        <div className="mt-5 flex flex-wrap gap-5">
                            <div className="flex gap-2 basis-1/5">
                                <p className="font-semibold ">Invited By:</p>
                                <p className="capitalize cursor-pointer text-primary font-bold">{submissionData?.isInvited?.invitedBy?.username}</p>
                            </div>
                            <div className="flex gap-2 basis-1/3">
                                <p className="font-semibold ">Invited On:</p>
                                <p className="text-primary font-bold">
                                    {new Intl.DateTimeFormat("en-IN", {
                                        day: "2-digit",
                                        month: "2-digit",
                                        year: "numeric",
                                        hour: "2-digit",
                                        minute: "2-digit",
                                        weekday: "short",
                                    }).format(new Date(submissionData?.isInvited?.createdAt))}
                                </p>
                            </div>
                        </div>
                    )
                }
                <div className="mt-5 flex items-start justify-between gap-5">
                    <div className="flex flex-col gap-2 items-start justify-between w-full">
                        <div className="flex gap-2">
                            <p className="font-semibold ">Challenge Submission Points:</p>
                            <p className="capitalize text-primary font-bold">{challengeData.points}</p>
                        </div>
                        <div className="flex gap-2">
                            <p className="font-semibold ">Earned Submission Points:</p>
                            {/* <p className="capitalize text-primary font-bold">{submissionData.allocatedPoints}</p> */}
                            <p className="capitalize text-primary font-bold">{
                                // submissionData.reviewStatus === 'submitted' ? submissionData.allocatedPoints : 
                                allocatedChallengePoints}</p>
                        </div>
                    </div>
                    <div className="flex flex-col gap-2 items-start justify-between w-full">
                        <div className="flex gap-2">
                            <p className="font-semibold ">Challenge Bonus Points:</p>
                            <p className="capitalize text-primary font-bold">{challengeData.bonusPoints || 0}</p>
                        </div>
                        <div className="flex gap-2">
                            <p className="font-semibold ">Earned Bonus Points:</p>
                            <p className="capitalize text-primary font-bold">{submissionData.allocatedBonusPoints || bonusPoints}</p>
                        </div>
                    </div>
                    <div className="flex flex-col gap-2 items-start justify-between w-full">
                        <div className="flex gap-2">
                            <p className="font-semibold ">Projected Carbon Saving Points:</p>
                            <p className="capitalize text-primary font-bold">{challengeData.carbonSavingPoints || 0}</p>
                        </div>
                        <div className="flex gap-2">
                            <p className="font-semibold ">Actual Carbon Saving Points:</p>
                            <p className="capitalize text-primary font-bold">{submissionData.actualCarbonSavingPoints || 0}</p>
                        </div>
                    </div>
                </div>
                <div className="flex flex-col gap-2 items-center justify-between w-full mt-5">
                    <div className="flex gap-2">
                        <p className="font-semibold ">Total Challenge Points:</p>
                        <p className="capitalize text-primary font-bold">{challengeData.points + challengeData.bonusPoints + challengeData?.carbonSavingPoints}</p>
                    </div>
                    <div className="flex gap-2">
                        <p className="font-semibold ">Total Earned Points:</p>
                        {/* <p className="capitalize text-primary font-bold">{submissionData.allocatedPoints + submissionData.allocatedBonusPoints}</p> */}
                        <p className="capitalize text-primary font-bold">{(parseInt(allocatedChallengePoints) + parseInt(bonusPoints) + parseInt(submissionData?.actualCarbonSavingPoints) || 0)
                            // || submissionData.allocatedPoints + submissionData.allocatedBonusPoints
                        }
                        </p>
                    </div>
                </div>
                {/* <p className="font-semibold text-center mt-2 cursor-pointer underline" onClick={() => setShowCarbonPointsCalculation(!showCarbonPointsCalculation)}>{showCarbonPointsCalculation ? "Hide" : "Show"} Carbon Savings Data</p>
                {
                    showCarbonPointsCalculation && (
                        <div className="mt-5">
                            <div className="flex items-center justify-between mt-2">
                                <h2>Projected Carbon Saving KGs:</h2>
                                <h2 className="text-primary font-bold">{challengeData && challengeData?.carbonEmissionCount || 0} KGs</h2>
                            </div>
                            <div className="flex items-center justify-between mt-2">
                                <h2>Actual Carbon Saving:</h2>
                                <h2 className="text-primary font-bold">
                                    {submissionData?.carbonEmissionCount || 0} KGs
                                </h2>
                            </div>
                            <div className="flex items-center justify-between mt-2">
                                <h2>Points to be assigned per kg:</h2>
                                <h2 className="text-primary font-bold">{homePageConfig && homePageConfig?.carbonSavingPointsPerKg}</h2>
                            </div>
                            <div className="flex items-center justify-between mt-2">
                                <h2>Actual Carbon Saving Points:</h2>
                                <h2 className="text-primary font-bold">
                                    {(submissionData?.actualCarbonSavingPoints)}
                                </h2>
                            </div>
                        </div>
                    )
                } */}
            </div>

            {/* Modal for Challenge Details */}
            <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)}>
                <h2 className="text-xl font-bold mb-4">Challenge Details</h2>
                <div className="flex flex-wrap gap-5">
                    <div className="mt-3 flex gap-2 basis-1/3">
                        <p className="font-semibold">Challenge Name:</p>
                        <p className="font-normal capitalize">{data && data?.challenge?.name}</p>
                    </div>
                    <div className="mt-3 flex gap-2 basis-1/3">
                        <p className="font-semibold">Challenge Category:</p>
                        <p className="font-normal capitalize">{data && data?.challenge?.category?.name}</p>
                    </div>
                    <div className="mt-3 flex gap-2 basis-1/3">
                        <p className="font-semibold">Challenge Idea Suggested By:</p>
                        <p className="font-normal capitalize">{data && data?.challenge?.suggestedBy?.username}</p>
                    </div>
                    <div className="mt-3 flex gap-2 basis-1/3">
                        <p className="font-semibold">Challenge Complexity:</p>
                        <p className="font-normal capitalize">{data && data?.challenge?.complexity}</p>
                    </div>
                    <div className="mt-3 flex gap-2 basis-1/3">
                        <p className="font-semibold">Inclusive:</p>
                        <p className="font-normal capitalize">{data && data?.challenge?.inclusive ? "Yes" : "No"}</p>
                    </div>
                    <div className="mt-3 flex gap-2 basis-1/3">
                        <p className="font-semibold">Type Of Challenge:</p>
                        <p className="font-normal capitalize">{data && data?.challenge?.recurrent}</p>
                    </div>
                    <div className="mt-3 flex gap-2 basis-1/3">
                        <p className="font-semibold">Challenge Submission Frequency:</p>
                        <p className="font-normal capitalize">{data && '-'}</p>
                    </div>
                    <div className="mt-3 flex gap-2 basis-1/3">
                        <p className="font-semibold">Type Of Frequency:</p>
                        <p className="font-normal capitalize">{data && data?.challenge?.typeOfFrequency ? data?.challenge?.typeOfFrequency : '-'}</p>
                    </div>
                    <div className="mt-3 flex gap-2 basis-1/3">
                        <p className="font-semibold">Frequency:</p>
                        <p className="font-normal capitalize">{data && data?.challenge?.frequency ? data?.challenge?.frequency : '-'}</p>
                    </div>
                    <div className="mt-3 flex gap-2 basis-1/3">
                        <p className="font-semibold">Start Date:</p>
                        <p className="font-normal capitalize">{data && new Date(data?.challenge?.recurrentDuration?.startDate).toDateString()}</p>
                    </div>
                    <div className="mt-3 flex gap-2 basis-1/3">
                        <p className="font-semibold">End Date</p>
                        <p className="font-normal capitalize">{data && new Date(data?.challenge?.recurrentDuration?.endDate).toDateString()}</p>
                    </div>
                </div>
            </Modal>

            {/* User Details */}
            <Modal isOpen={isUserModalOpen} onClose={() => setIsUserModalOpen(false)}>
                <h2 className="text-xl font-bold mb-4">User Details</h2>
                <div className="flex gap-5">
                    <div className="mt-3 flex gap-2 basis-1/3">
                        <p className="font-semibold">First Name:</p>
                        <p className="font-normal capitalize">{data && data.user.name.firstName}</p>
                    </div>
                    <div className="mt-3 flex gap-2 basis-1/3">
                        <p className="font-semibold">Middle Name:</p>
                        <p className="font-normal capitalize">{data && data.user.name.middleName ? data.user.name.middleName : '-'}</p>
                    </div>
                    <div className="mt-3 flex gap-2 basis-1/3">
                        <p className="font-semibold">Last Name:</p>
                        <p className="font-normal capitalize">{data && data.user.name.lastName}</p>
                    </div>
                </div>
                <div className="flex gap-5">
                    <div className="mt-3 flex gap-2 basis-1/3">
                        <p className="font-semibold">Username:</p>
                        <p className="font-normal capitalize">{data && data.user.username}</p>
                    </div>
                    <div className="mt-3 flex gap-2 basis-1/3">
                        <p className="font-semibold">Display Name:</p>
                        <p className="font-normal capitalize">{data && data.user.displayName ? data.user.displayName : '-'}</p>
                    </div>
                    <div className="mt-3 flex gap-2 basis-1/3">
                        <p className="font-semibold">Email:</p>
                        <p className="font-normal">{data && data.user.email ? data.user.email : '-'}</p>
                    </div>
                </div>
                <div className="flex gap-5">
                    <div className="mt-3 flex gap-2 basis-1/3">
                        <p className="font-semibold">Phone No:</p>
                        <p className="font-normal capitalize">{data && data.user.mobile ? data.user.mobile : '-'}</p>
                    </div>
                    <div className="mt-3 flex gap-2 basis-1/3">
                        <p className="font-semibold">Date of Birth:</p>
                        <p className="font-normal capitalize">{data && data.user.dob ? data.user.dob : '-'}</p>
                    </div>
                    <div className="mt-3 flex gap-2 basis-1/3">
                        <p className="font-semibold">User Type:</p>
                        <p className="font-normal capitalize">{data && data.user.userType ? data.user.userType : '-'}</p>
                    </div>
                </div>
                <div className="flex gap-5">
                    <div className="mt-3 flex gap-2 basis-1/3">
                        <p className="font-semibold">Address:</p>
                        <p className="font-normal capitalize">{data && data.user?.location?.address ? data.user?.location?.address : '-'}</p>
                    </div>
                    <div className="mt-3 flex gap-2 basis-1/3">
                        <p className="font-semibold">Landmark:</p>
                        <p className="font-normal capitalize">{data && data.user?.location?.landmark ? data.user?.location?.landmark : '-'}</p>
                    </div>
                    <div className="mt-3 flex gap-2 basis-1/3">
                        <p className="font-semibold">State:</p>
                        <p className="font-normal capitalize">{data && data.user?.location?.state ? data.user?.location?.state : '-'}</p>
                    </div>
                </div>
                <div className="flex gap-5">
                    <div className="mt-3 flex gap-2 basis-1/3">
                        <p className="font-semibold">City:</p>
                        <p className="font-normal capitalize">{data && data.user?.location?.city ? data.user?.location?.city : '-'}</p>
                    </div>
                    <div className="mt-3 flex gap-2 basis-1/3">
                        <p className="font-semibold">Pincode:</p>
                        <p className="font-normal capitalize">{data && data.user?.location?.pincode ? data.user?.location?.pincode : '-'}</p>
                    </div>
                </div>
                <div className="flex gap-5">
                    <div className="mt-3 flex gap-2 basis-1/3">
                        <p className="font-semibold">Challenges Suggested:</p>
                        <p className="font-normal capitalize">{data && data.user?.challengesCreated?.length ? data.user?.challengesCreated?.length : '0'}</p>
                    </div>
                    <div className="mt-3 flex gap-2 basis-1/3">
                        <p className="font-semibold">Challenges Participated:</p>
                        <p className="font-normal capitalize">{data && data.user?.challengesParticipated?.length ? data.user?.challengesParticipated?.length : '-'}</p>
                    </div>
                    <div className="mt-3 flex gap-2 basis-1/3">
                        <p className="font-semibold">Points:</p>
                        <p className="font-normal capitalize">{data && data.user?.points ? data.user?.points : '-'}</p>
                    </div>
                </div>
            </Modal>

            {/* Final Submit Modal */}
            {/* <Modal isOpen={isFinalModalOpen} onClose={() => setIsFinalModalOpen(false)} className="max-h-96 overflow-y-auto">
                <h2 className="text-xl font-bold mb-4">Are you sure to submit?</h2>
                <div className="mt-5 flex items-start justify-between gap-5">
                    <div className="flex flex-col gap-2 items-start justify-between w-full">
                        <div className="flex gap-2">
                            <p className="font-semibold ">Total Challenge Points:</p>
                            <p className="capitalize text-primary font-bold">{challengeData.points + challengeData.bonusPoints}</p>
                        </div>
                        <div className="flex gap-2">
                            <p className="font-semibold ">Total Earned Points:</p>
                            <p className="capitalize text-primary font-bold">{submissionData.allocatedPoints + submissionData.allocatedBonusPoints}</p>
                            <p className="capitalize text-primary font-bold">{(parseInt(allocatedChallengePoints) + parseInt(bonusPoints)) || submissionData.allocatedPoints + submissionData.allocatedBonusPoints}</p>
                        </div>
                    </div>
                    <div className="flex flex-col gap-2 items-start justify-between w-full">
                        <div className="flex gap-2">
                            <p className="font-semibold ">Challenge Points:</p>
                            <p className="capitalize text-primary font-bold">{challengeData.points}</p>
                        </div>
                        <div className="flex gap-2">
                            <p className="font-semibold ">Earned Challenge Points:</p>
                            <p className="capitalize text-primary font-bold">{submissionData.allocatedPoints}</p>
                            <p className="capitalize text-primary font-bold">{submissionData.reviewStatus === 'submitted' ? submissionData.allocatedPoints : allocatedChallengePoints}</p>
                        </div>
                    </div>
                    <div className="flex flex-col gap-2 items-start justify-between w-full">
                        <div className="flex gap-2">
                            <p className="font-semibold ">Challenge Bonus Points:</p>
                            <p className="capitalize text-primary font-bold">{challengeData.bonusPoints || 0}</p>
                        </div>
                        <div className="flex gap-2">
                            <p className="font-semibold ">Earned Bonus Points:</p>
                            <p className="capitalize text-primary font-bold">{submissionData.allocatedBonusPoints || bonusPoints}</p>
                        </div>
                    </div>
                </div>
                <div className="mt-2.5 flex flex-col items-start justify-between gap-2">
                    <div className="flex gap-2">
                        <p className="font-semibold ">Total Carbon Savings In This Challenge:</p>
                        <p className="capitalize text-primary font-bold">{challengeData?.carbonEmissionCount} KG</p>
                    </div>
                    <div className="flex gap-2">
                        <p className="font-semibold ">Carbon Savings Points For This Challenge:</p>
                        <p className="capitalize text-primary font-bold">{challengeData?.carbonSavingPoints} Points/KG</p>
                    </div>
                </div>
                <div className="flex flex-row-reverse items-start justify-between mt-5 gap-3">
                    <div className="basis-1/2">
                        <InputField
                            label="User's Carbon Savings"
                            name="carbonEmissionCount"
                            value={carbonEmissionCount}
                            placeholder="Enter User's Carbon Savings in KG"
                            type="number"
                            onChange={(e) => {
                                setCarbonEmissionCount(e.target.value);
                            }}
                        />
                    </div>
                    <div className="basis-1/2">
                        <RichTextEditor
                            label="Carbon Savings Calculation"
                            value={carbonEmissionInfo}
                            placeholder="Enter Carbon Savings Calculation"
                            onChange={setCarbonEmissionInfo}
                        />
                    </div>
                </div>
                <div className="flex items-center justify-between mt-5">
                    {
                        submissionData.bonusPoints !== 0 ? (
                            <div className="flex justify-between items-center w-60 mt-5">
                                <p className="inline font-semibold min-w-24">Bonus Points:</p>
                                <input
                                    type="number"
                                    className="border border-navBtn rounded-md p-1 ml-2 w-40"
                                    placeholder="Enter Bonus Points"
                                    value={bonusPoints}
                                    max={challengeData.bonusPoints}
                                    min={0}
                                    onChange={(e) => {
                                        setBonusPoints(e.target.value);
                                    }}
                                />
                            </div>) : <div><p className="inline font-semibold min-w-24">This challenge has no bonus points.</p></div>
                    }
                    <div className="flex gap-3 basis-1/2">
                        <InputField
                            label="Carbon Savings"
                            name="carbonEmissionCount"
                            value={carbonEmissionCount}
                            placeholder="Enter Carbon Savings in KG"
                            type="string"
                            onChange={(e) => {
                                setCarbonEmissionCount(e.target.value);
                                setSubmissionData({ ...submissionData, carbonEmissionCount: e.target.value });
                            }}
                        />
                        <RichTextEditor
                            label="Carbon Savings Calculation"
                            value={carbonEmissionInfo}
                            placeholder="Enter Carbon Savings Calculation"
                            onChange={setCarbonEmissionInfo}
                        />
                    </div>
                    <div></div>
                    <div className="flex gap-3 items-center justify-between self-end">
                        <ActionButton
                            variant={'cancel'}
                            text={'Cancel'}
                            handleSubmit={() => setIsFinalModalOpen(false)}
                        />
                        <ActionButton
                            variant={'submit'}
                            text={'Submit'}
                            handleSubmit={() => {
                                setIsFinalModalOpen(false);
                                // console.log(submissionData);
                                submitChallengeVerification({
                                    submissionData,
                                    userChallengeSubmissionId: state._id,
                                    bonusPoints,
                                    carbonEmissionInfo,
                                    carbonEmissionCount,
                                }, setSubmissionData, toast);
                            }}
                        />
                    </div>
                </div>
            </Modal> */}

            <Modal isOpen={isFinalModalOpen} onClose={() => setIsFinalModalOpen(false)} className="max-h-96 overflow-y-auto">
                <h2 className="text-xl font-bold mb-4">Do you want to accept this submission?</h2>
                <div className="mt-5 flex flex-col items-start justify-between gap-3">
                    <div className="flex items-start w-full gap-5">
                        <div className="flex gap-2 basis-1/2">
                            <p className="font-semibold ">Submitted By:</p>
                            <p className="capitalize text-primary font-bold">{data && data.user.username}</p>
                        </div>
                        <div className="flex gap-2 basis-1/2">
                            <p className="font-semibold ">Submitted On:</p>
                            <p className="capitalize text-primary font-bold">{new Intl.DateTimeFormat("en-IN", {
                                day: "2-digit",
                                month: "2-digit",
                                year: "numeric",
                                hour: "2-digit",
                                minute: "2-digit",
                                weekday: "short",
                            }).format(submissionData?.createdAt && new Date(submissionData?.createdAt))}</p>
                        </div>
                    </div>
                    <div className="flex items-start w-full gap-5">
                        <div className="flex gap-2 basis-1/2">
                            <p className="font-semibold ">Total Questions:</p>
                            <p className="capitalize text-primary font-bold">
                                {submissionData?.pagesAndQuestions?.reduce((total, page) => total + page.questions.length, 0)}
                            </p>
                        </div>
                        <div className="flex gap-2 basis-1/2">
                            <p className="font-semibold ">Attempted Questions:</p>
                            <p className="capitalize text-primary font-bold">
                                {submissionData?.pagesAndQuestions?.reduce((total, page) => total + page.questions.filter(q => q.answer).length, 0)}
                            </p>
                        </div>
                    </div>
                    <div className="flex items-start w-full gap-5">
                        <div className="flex gap-2 basis-1/2">
                            <p className="font-semibold ">Answers Accepted:</p>
                            <p className="capitalize text-primary font-bold">
                                {submissionData?.pagesAndQuestions?.reduce((total, page) => total + page.questions.filter(q => q.isApproved).length, 0)}
                            </p>
                        </div>
                        <div className="flex gap-2 basis-1/2">
                            <p className="font-semibold ">Answers Rejected:</p>
                            <p className="capitalize text-primary font-bold">
                                {submissionData?.pagesAndQuestions?.reduce((total, page) => total + page.questions.filter(q => q.isRejected).length, 0)}
                            </p>
                        </div>
                    </div>
                    <div className="flex items-start w-full gap-5">
                        <div className="flex gap-2 basis-1/2">
                            <p className="font-semibold ">Total Points:</p>
                            <p className="capitalize text-primary font-bold">
                                {challengeData.points + challengeData.bonusPoints}
                            </p>
                        </div>
                        <div className="flex gap-2 basis-1/2">
                            <p className="font-semibold ">User Points:</p>
                            <p className="capitalize text-primary font-bold">
                                {(parseInt(allocatedChallengePoints) + parseInt(bonusPoints))}
                            </p>
                        </div>
                    </div>
                </div>
                {/* Yes/No Buttons */}
                <div className="flex items-center justify-center gap-10 mt-5">
                    <label className="flex items-center gap-2">
                        <input
                            type="radio"
                            name={submissionDecision}
                            value="yes"
                            className="form-radio text-primary"
                            checked={submissionDecision === "yes"}
                            onChange={(e) => setSubmissionDecision(e.target.value)}
                        />
                        <span>Yes</span>
                    </label>
                    <label className="flex items-center gap-2">
                        <input
                            type="radio"
                            name={submissionDecision}
                            value="no"
                            className="form-radio text-primary"
                            checked={submissionDecision === "no"}
                            onChange={(e) => setSubmissionDecision(e.target.value)}
                        />
                        <span>No</span>
                    </label>
                </div>
                <div>
                    <InputField
                        name={'rejectionReason'}
                        label={'Rejection Reason'}
                        value={rejectionReason}
                        onChange={(e) => setRejectionReason(e.target.value)}
                        placeholder={'Enter Rejection Reason'}
                        required={submissionDecision === 'no'}
                    />
                    {
                        submissionData?.aiReview?.suggestion?.overall?.userMessage && (
                            <button
                                type="button"
                                className="mt-1 px-2 py-0.5 rounded border border-primary text-primary text-sm hover:bg-primary hover:text-white"
                                onClick={() => setRejectionReason(submissionData.aiReview.suggestion.overall.userMessage)}
                            >
                                Use AI message: "{submissionData.aiReview.suggestion.overall.userMessage}"
                            </button>
                        )
                    }
                    {
                        rejectionError && (
                            <p className="text-red text-sm font-semibold mt-2">Please provide a rejection reason.</p>
                        )
                    }
                </div>
                <div className="flex items-center justify-center mt-5 gap-5">
                    <ActionButton
                        text={'OK'}
                        variant={'submit'}
                        handleSubmit={() => {
                            setRejectionError(false);

                            // Validate submission data exists
                            if (!submissionData || !submissionData.pagesAndQuestions) {
                                toast.error('Submission data is missing. Please reload the page.');
                                return;
                            }

                            if (submissionDecision === 'no' && !rejectionReason) {
                                setRejectionError(true);
                            } else {
                                // If the entire submission is rejected
                                if (submissionDecision === 'no') {
                                    submissionData.pagesAndQuestions.forEach(page => {
                                        page.questions.forEach(question => {
                                            question.isApproved = false;
                                            question.isRejected = true;
                                            question.remarks = rejectionReason;
                                            question.allocatedPoints = 0;
                                        });
                                    });
                                    submissionData.reviewStatus = 'rejected';
                                    submissionData.allocatedPoints = 0;
                                    submissionData.allocatedBonusPoints = 0;
                                    submissionData.actualCarbonSavingPoints = 0;
                                    submissionData.carbonEmissionCount = 0;
                                    submissionData.carbonEmissionInfo = '';
                                    submitChallengeVerification({
                                        submissionData,
                                        userChallengeSubmissionId: state._id,
                                        bonusPoints,
                                        rejectionReason,
                                    }, true, setSubmissionData, toast, navigate);
                                }
                                // If the submission is accepted
                                else {
                                    console.log('Submitting verification with data:', {
                                        submissionData,
                                        userChallengeSubmissionId: state._id,
                                        bonusPoints,
                                        rejectionReason,
                                        hasPagesAndQuestions: !!submissionData.pagesAndQuestions,
                                        pagesCount: submissionData.pagesAndQuestions?.length
                                    });
                                    submitChallengeVerification({
                                        submissionData,
                                        userChallengeSubmissionId: state._id,
                                        bonusPoints,
                                        rejectionReason,
                                    }, false, setSubmissionData, toast, navigate);
                                    setTimeout(() => {
                                        navigate('/submissions/challenge-submission-details-state', { state: { submissionId: state._id } });
                                    }, 2000);
                                }
                            }
                        }}
                    />
                    <ActionButton
                        text={'Cancel'}
                        variant={'cancel'}
                        handleSubmit={() => setIsFinalModalOpen(false)}
                    />
                </div>
            </Modal>

            <AiReviewPanel aiReview={submissionData?.aiReview} />

            <div className="flex w-full justify-between gap-2">
                <div className='h-fit my-5 flex flex-col gap-2 basis-1/4 bg-white rounded-md shadow-[0px_0px_25px_rgba(94,92,154,0.1)] px-3 py-3'>
                    <h1 className="text-primary text-xl">Pages to be checked:</h1>
                    {
                        challengeData && challengeData.pagesAndQuestions.map(page => (
                            <button
                                className={`font-bold text-base rounded-md py-2 border-2 ${selectedTab === page._id ? 'bg-white' : 'bg-navBtn'} ${selectedTab === page._id ? 'border-primary' : 'border-navBtn'}`}
                                onClick={() => setSelectedTab(page._id)}
                                key={page._id}
                            >
                                Page {challengeData.pagesAndQuestions.indexOf(page) + 1}: {page.title || 'Submission Page'}</button>
                        ))
                    }
                </div>
                {/* Submissions */}
                {
                    submissionData && submissionData.pagesAndQuestions.map((page, pageIndex) => {
                        if (selectedTab !== page.pageId) return null;
                        return (
                            <div className="flex flex-col gap-0 flex-1 bg-white rounded-md shadow-[0px_0px_25px_rgba(94,92,154,0.1)] px-3 py-3 mt-5" key={pageIndex}>
                                <h1 className="text-primary text-xl"> Page {submissionData.pagesAndQuestions.indexOf(page) + 1}: {page.title || 'Submission Page'} ({page.questions.length} Questions)</h1>
                                {page.questions.map((question, questionIndex) => {
                                    if (challengeData.pagesAndQuestions[pageIndex].questions[questionIndex].questionType === "media") {
                                        return (
                                            <div key={questionIndex} className="border border-navBtn rounded p-3 mt-5 flex flex-col w-full">
                                                <div className="flex justify-between items-center gap-2">
                                                    <div className="rounded-md basis-1/2 cursor-pointer flex justify-between items-center" onClick={() => toggleQuestion(pageIndex, questionIndex)}>
                                                        <h1 className='flex items-center justify-center'>{expandedQuestionIndex[pageIndex] === questionIndex ? <FaAngleDown /> : <FaAngleRight />}{`Question-${questionIndex + 1}`}: <p className="font-medium capitalize text-secondary">&nbsp;(Type: {challengeData.pagesAndQuestions[pageIndex].questions[questionIndex].questionType})</p> </h1>
                                                    </div>
                                                    <div className='flex gap-2 basis-1/4'>
                                                        <p className={'text-secondary'}>Points: {challengeData.pagesAndQuestions[pageIndex].questions[questionIndex].points}</p>
                                                    </div>
                                                    <div className='flex gap-2 basis-1/4'>
                                                        <p className={'text-secondary'}>Allocated Points: {question.allocatedPoints}</p>
                                                    </div>
                                                    <div className='flex gap-2 basis-1/12'>
                                                        <p className={`font-bold ${question.isApproved ? ` text-primary` : `text-red`}`}>
                                                            {question.isApproved ? "Approved" : question.isRejected ? "Rejected" : question.isEmpty ? "Empty" : "Pending"}
                                                        </p>
                                                    </div>
                                                    {/* {
                                                        submissionData.reviewStatus === 'submitted' &&
                                                        <div className='flex gap-2 basis-1/12'>
                                                            <p
                                                                className={`font-bold text-secondary px-1 rounded cursor-pointer ${disabledControl[`${page.pageId}-${questionIndex}`] ? 'bg-navBtn' : 'bg-white'}`}
                                                                onClick={() => {
                                                                    disabledControl[`${page.pageId}-${questionIndex}`] = !disabledControl[`${page.pageId}-${questionIndex}`];
                                                                    setDisabledControl({ ...disabledControl });
                                                                }}
                                                            >Edit</p>
                                                        </div>
                                                    } */}
                                                </div>
                                                <AiAnswerNote aiReview={submissionData?.aiReview} questionId={question.questionId} onApply={(pts, msg) => {
                                                    setAllocatedPoints(prev => ({ ...prev, [`${page.pageId}-${questionIndex}`]: pts }));
                                                    if (msg) setRemarks(prev => ({ ...prev, [`${page.pageId}-${questionIndex}`]: msg }));
                                                }} />
                                                {expandedQuestionIndex[pageIndex] === questionIndex && (
                                                    <div className="flex justify-between items-center">
                                                        <div className="w-full mt-2 flex flex-col gap-3">
                                                            <div className="flex gap-2 justify-between">
                                                                <div className="w-full">
                                                                    <div className="mb-2">
                                                                        <p className="inline font-semibold">Title:</p>
                                                                        <p className="font-medium capitalize">{challengeData.pagesAndQuestions[pageIndex].questions[questionIndex].title ? challengeData.pagesAndQuestions[pageIndex].questions[questionIndex].title : "-"}</p>
                                                                    </div>
                                                                    <div className="mt-2">
                                                                        <p className="inline font-semibold">Description:</p>
                                                                        <p className="font-medium" dangerouslySetInnerHTML={{ __html: challengeData.pagesAndQuestions[pageIndex].questions[questionIndex].description }}></p>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            <hr className=" place-item-center border-secondary" />
                                                            <div className="mt-2">
                                                                {renderUserAnswer(pageIndex, questionIndex)}
                                                            </div>
                                                            <p>Answer Required: {challengeData?.pagesAndQuestions[pageIndex]?.questions[questionIndex]?.submissionRequired ? "Yes" : "No"}</p>
                                                            <p>Self Image Required: {challengeData?.pagesAndQuestions[pageIndex]?.questions[questionIndex]?.selfImageRequired ? "Yes" : "No"}</p>
                                                            
                                                            {/* Face Verification Section */}
                                                            {challengeData?.pagesAndQuestions[pageIndex]?.questions[questionIndex]?.selfImageRequired && 
                                                             challengeData?.pagesAndQuestions[pageIndex]?.questions[questionIndex]?.questionType === 'media' &&
                                                             challengeData?.pagesAndQuestions[pageIndex]?.questions[questionIndex]?.mediaType === 'image' &&
                                                             submissionData?.pagesAndQuestions[pageIndex]?.questions[questionIndex]?.answer && (
                                                                <FaceComparisonResult 
                                                                    verification={faceVerificationResults[`${page.pageId}-${questionIndex}`]}
                                                                    loading={faceVerificationLoading[`${page.pageId}-${questionIndex}`]}
                                                                    error={faceVerificationResults[`${page.pageId}-${questionIndex}`]?.error}
                                                                />
                                                            )}
                                                            
                                                            <div className="flex items-center gap-2">
                                                                <AllocatePoints
                                                                    key={`${page.pageId}-${questionIndex}-${allocatedPoints[`${page.pageId}-${questionIndex}`] || 0}`}
                                                                    required={true}
                                                                    // allocatedPoints={(question.isApproved || question.isRejected || question?.isEmpty) ? allocatedPoints[`${page.pageId}-${questionIndex}`] : challengeData.pagesAndQuestions[pageIndex].questions[questionIndex].points}
                                                                    allocatedPoints={allocatedPoints[`${page.pageId}-${questionIndex}`] || 0}
                                                                    setAllocatedPoints={(points) => setAllocatedPoints(prev => ({ ...prev, [`${page.pageId}-${questionIndex}`]: parseInt(points) }))}
                                                                    max={challengeData.pagesAndQuestions[pageIndex].questions[questionIndex].points}
                                                                    disable={disabledControl}
                                                                />
                                                                <div className="flex justify-between items-center w-full">
                                                                    <p className="inline font-semibold">Remarks:</p>
                                                                    <input
                                                                        type="text"
                                                                        className="border border-navBtn rounded-md p-1 ml-2 w-full"
                                                                        placeholder="Write Remarks"
                                                                        value={remarks[`${page.pageId}-${questionIndex}`] || ''}
                                                                        onChange={(e) => {
                                                                            setRemarks(prev => ({ ...prev, [`${page.pageId}-${questionIndex}`]: e.target.value }));
                                                                            setRemarksErr(false);
                                                                        }}
                                                                        disabled={disabledControl}
                                                                    />
                                                                </div>
                                                            </div>
                                                            {
                                                                remarksErr && <p className="text-red text-sm font-bold self-end -mt-3">{'Please enter remarks'}</p>
                                                            }
                                                            <div className="flex gap-2 mt-2">
                                                                <button
                                                                    className={`flex-1 bg-redDark hover:bg-hoverRed text-white font-bold text-base py-2 rounded-md cursor-pointer ${disabledControl ? 'opacity-50 cursor-not-allowed' : ''}`}
                                                                    disabled={submissionData.reviewStatus === 'submitted' && disabledControl}
                                                                    // onClick={() => !question.isRejected && handleReject(question._id, page.pageId, questionIndex)}
                                                                    onClick={() => {
                                                                        if (question.isRejected) return;
                                                                        const remark = remarks[`${page.pageId}-${questionIndex}`];
                                                                        if (!remark) {
                                                                            setRemarksErr(true);
                                                                            return;
                                                                        }
                                                                        submissionData.pagesAndQuestions[pageIndex].questions[questionIndex].isApproved = false;
                                                                        submissionData.pagesAndQuestions[pageIndex].questions[questionIndex].isRejected = true;
                                                                        submissionData.pagesAndQuestions[pageIndex].questions[questionIndex].isEmpty = false;
                                                                        submissionData.pagesAndQuestions[pageIndex].questions[questionIndex].allocatedPoints = 0;
                                                                        setAllocatedPoints(prev => ({ ...prev, [`${page.pageId}-${questionIndex}`]: 0 }));
                                                                        submissionData.pagesAndQuestions[pageIndex].questions[questionIndex].remarks = remarks[`${page.pageId}-${questionIndex}`];
                                                                        setSubmissionData({ ...submissionData });
                                                                        calculateTotalAllocatedPoints();
                                                                        // Move to the next question or page
                                                                        if (questionIndex < page.questions.length - 1) {
                                                                            setExpandedQuestionIndex(prev => ({ ...prev, [pageIndex]: questionIndex + 1 }));
                                                                        } else if (pageIndex < submissionData.pagesAndQuestions.length - 1) {
                                                                            setSelectedTab(submissionData.pagesAndQuestions[pageIndex + 1].pageId);
                                                                            setExpandedQuestionIndex(prev => ({ ...prev, [pageIndex + 1]: 0 }));
                                                                        }
                                                                    }}
                                                                >
                                                                    Reject
                                                                </button>
                                                                {
                                                                    !challengeData?.pagesAndQuestions[pageIndex]?.questions[questionIndex]?.submissionRequired && (
                                                                        <button
                                                                            className={`flex-1 bg-primary hover:bg-primaryHoverDark text-white font-bold text-base py-2 rounded-md ${disabledControl ? 'opacity-50 cursor-not-allowed' : ''}`}
                                                                            disabled={submissionData.reviewStatus === 'submitted' && disabledControl}
                                                                            // onClick={() => !question.isApproved && handleApprove(question._id, allocatedPoints[`${page.pageId}-${questionIndex}`], remarks[`${page.pageId}-${questionIndex}`])}
                                                                            onClick={() => {
                                                                                // Automatically set points to 0 and remarks to "Not Submitted!"
                                                                                submissionData.pagesAndQuestions[pageIndex].questions[questionIndex].isApproved = false;
                                                                                submissionData.pagesAndQuestions[pageIndex].questions[questionIndex].isRejected = false;
                                                                                submissionData.pagesAndQuestions[pageIndex].questions[questionIndex].isEmpty = true;
                                                                                submissionData.pagesAndQuestions[pageIndex].questions[questionIndex].allocatedPoints = 0;
                                                                                // Update allocated points state properly
                                                                                setTimeout(() => {
                                                                                    setAllocatedPoints(prev => ({ ...prev, [`${page.pageId}-${questionIndex}`]: 0 }));
                                                                                }, 50);
                                                                                // Automatically set remarks to "Not Submitted!"
                                                                                const notSubmittedRemark = "Not Submitted!";
                                                                                setRemarks(prev => ({ ...prev, [`${page.pageId}-${questionIndex}`]: notSubmittedRemark }));
                                                                                submissionData.pagesAndQuestions[pageIndex].questions[questionIndex].remarks = notSubmittedRemark;
                                                                                setSubmissionData({ ...submissionData });
                                                                                calculateTotalAllocatedPoints();
                                                                                // Clear any remarks error
                                                                                setRemarksErr('');
                                                                                // Move to the next question or page
                                                                                if (questionIndex < page.questions.length - 1) {
                                                                                    setExpandedQuestionIndex(prev => ({ ...prev, [pageIndex]: questionIndex + 1 }));
                                                                                } else if (pageIndex < submissionData.pagesAndQuestions.length - 1) {
                                                                                    setSelectedTab(submissionData.pagesAndQuestions[pageIndex + 1].pageId);
                                                                                    setExpandedQuestionIndex(prev => ({ ...prev, [pageIndex + 1]: 0 }));
                                                                                }
                                                                            }}
                                                                        >
                                                                            Not Submitted
                                                                        </button>
                                                                    )
                                                                }
                                                                <button
                                                                    className={`flex-1 bg-primary hover:bg-primaryHoverDark text-white font-bold text-base py-2 rounded-md ${disabledControl ? 'opacity-50 cursor-not-allowed' : ''}`}
                                                                    disabled={submissionData.reviewStatus === 'submitted' && disabledControl}
                                                                    // onClick={() => !question.isApproved && handleApprove(question._id, allocatedPoints[`${page.pageId}-${questionIndex}`], remarks[`${page.pageId}-${questionIndex}`])}
                                                                    onClick={() => {
                                                                        if (question.isApproved) return;
                                                                        submissionData.pagesAndQuestions[pageIndex].questions[questionIndex].isApproved = true;
                                                                        submissionData.pagesAndQuestions[pageIndex].questions[questionIndex].isRejected = false;
                                                                        submissionData.pagesAndQuestions[pageIndex].questions[questionIndex].isEmpty = false;
                                                                        submissionData.pagesAndQuestions[pageIndex].questions[questionIndex].allocatedPoints = allocatedPoints[`${page.pageId}-${questionIndex}`];
                                                                        submissionData.pagesAndQuestions[pageIndex].questions[questionIndex].remarks = remarks[`${page.pageId}-${questionIndex}`];
                                                                        submissionData.allocatedPoints = parseInt(submissionData.allocatedPoints) + allocatedPoints[`${page.pageId}-${questionIndex}`];
                                                                        setSubmissionData({ ...submissionData });
                                                                        setRemarksErr(false);
                                                                        calculateTotalAllocatedPoints();
                                                                        // Move to the next question or page
                                                                        if (questionIndex < page.questions.length - 1) {
                                                                            setExpandedQuestionIndex(prev => ({ ...prev, [pageIndex]: questionIndex + 1 }));
                                                                        } else if (pageIndex < submissionData.pagesAndQuestions.length - 1) {
                                                                            setSelectedTab(submissionData.pagesAndQuestions[pageIndex + 1].pageId);
                                                                            setExpandedQuestionIndex(prev => ({ ...prev, [pageIndex + 1]: 0 }));
                                                                        }
                                                                    }}
                                                                >
                                                                    Approve
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    }
                                    else {
                                        return (
                                            <div key={questionIndex} className="border border-navBtn rounded p-3 mt-5 flex flex-col w-full">
                                                <div className="flex justify-between items-center gap-2">
                                                    <div className="rounded-md cursor-pointer basis-1/2 flex justify-between items-center" onClick={() => toggleQuestion(pageIndex, questionIndex)}>
                                                        <h1 className='flex items-center justify-center'>{expandedQuestionIndex[pageIndex] === questionIndex ? <FaAngleDown /> : <FaAngleRight />}{`Question-${questionIndex + 1}`}: <p className="font-medium capitalize text-secondary">&nbsp;(Type: {challengeData.pagesAndQuestions[pageIndex].questions[questionIndex].questionType})</p> </h1>
                                                    </div>
                                                    <div className='flex gap-2 basis-1/4'>
                                                        <p className={'text-secondary'}>Points: {challengeData.pagesAndQuestions[pageIndex].questions[questionIndex].points}</p>
                                                    </div>
                                                    <div className='flex gap-2 basis-1/4'>
                                                        <p className={'text-secondary'}>Allocated Points: {question.allocatedPoints}</p>
                                                    </div>
                                                    <div className='flex gap-2 basis-1/12'>
                                                        <p className={`font-bold ${question.isApproved ? 'text-primary' : 'text-red'}`}>
                                                            {question.isApproved ? "Approved" : question.isRejected ? "Rejected" : question.isEmpty ? "Empty" : "Pending"}
                                                        </p>
                                                    </div>
                                                    {/* {
                                                        submissionData.reviewStatus === 'submitted' &&
                                                        <div className='flex gap-2 basis-1/12'>
                                                            <p
                                                                className={`font-bold text-secondary px-1 rounded cursor-pointer ${disabledControl[`${page.pageId}-${questionIndex}`] ? 'bg-navBtn' : 'bg-white'}`}
                                                                onClick={() => {
                                                                    disabledControl[`${page.pageId}-${questionIndex}`] = !disabledControl[`${page.pageId}-${questionIndex}`];
                                                                    setDisabledControl({ ...disabledControl });
                                                                    console.log(disabledControl);
                                                                }}
                                                            >Edit</p>
                                                        </div>
                                                    } */}
                                                </div>
                                                <AiAnswerNote aiReview={submissionData?.aiReview} questionId={question.questionId} onApply={(pts, msg) => {
                                                    setAllocatedPoints(prev => ({ ...prev, [`${page.pageId}-${questionIndex}`]: pts }));
                                                    if (msg) setRemarks(prev => ({ ...prev, [`${page.pageId}-${questionIndex}`]: msg }));
                                                }} />
                                                {expandedQuestionIndex[pageIndex] === questionIndex && (
                                                    <div className="flex justify-between items-center">
                                                        <div className="w-full mt-2 flex flex-col gap-3">
                                                            <div className="w-full flex gap-2 justify-between">
                                                                <div>
                                                                    <p className="inline font-semibold">Question:</p>
                                                                    <p className="font-medium" dangerouslySetInnerHTML={{ __html: challengeData.pagesAndQuestions[pageIndex].questions[questionIndex].question }}></p>
                                                                </div>
                                                            </div>
                                                            <hr className=" place-item-center border-secondary" />
                                                            {/* <div className="mt-2">
                                                                <p className="inline font-semibold">User's Answer:</p>
                                                                <p className="font-medium mt-1">{question.answer}</p>
                                                            </div> */}
                                                            {/* User Submitted Answer */}
                                                            {renderUserAnswer(pageIndex, questionIndex)}
                                                            <p>Answer Required: {challengeData?.pagesAndQuestions[pageIndex]?.questions[questionIndex]?.submissionRequired ? "Yes" : "No"}</p>
                                                            <div className="flex items-center gap-2">
                                                                <AllocatePoints
                                                                    key={`${page.pageId}-${questionIndex}-${allocatedPoints[`${page.pageId}-${questionIndex}`] || 0}`}
                                                                    required={true}
                                                                    allocatedPoints={allocatedPoints[`${page.pageId}-${questionIndex}`] || 0}
                                                                    setAllocatedPoints={(points) => setAllocatedPoints(prev => ({ ...prev, [`${page.pageId}-${questionIndex}`]: parseInt(points) }))}
                                                                    max={challengeData.pagesAndQuestions[pageIndex].questions[questionIndex].points}
                                                                    disable={disabledControl}
                                                                />
                                                                <div className="flex justify-between items-center w-full">
                                                                    <p className="inline font-semibold">Remarks:</p>
                                                                    <input
                                                                        type="text"
                                                                        className="border border-navBtn rounded-md p-1 ml-2 w-full disabled:cursor-not-allowed"
                                                                        placeholder="Write Remarks"
                                                                        value={remarks[`${page.pageId}-${questionIndex}`] || ''}
                                                                        onChange={(e) => {
                                                                            setRemarksErr(false);
                                                                            setRemarks(prev => ({ ...prev, [`${page.pageId}-${questionIndex}`]: e.target.value }));
                                                                        }}
                                                                        disabled={disabledControl}
                                                                    />
                                                                </div>
                                                            </div>
                                                            {
                                                                remarksErr && <p className="text-red text-sm font-bold self-end -mt-3">{'Please enter remarks'}</p>
                                                            }
                                                            <div className="flex gap-2 mt-2">
                                                                <button
                                                                    className={`flex-1 bg-redDark hover:bg-hoverRed text-white font-bold text-base py-2 rounded-md cursor-pointer ${disabledControl ? 'opacity-50 cursor-not-allowed' : ''}`}
                                                                    disabled={submissionData.reviewStatus === 'submitted' && disabledControl}
                                                                    // onClick={() => !question.isRejected && handleReject(question._id, page.pageId, pageIndex, questionIndex)}
                                                                    onClick={() => {
                                                                        // if (question.isRejected) return;
                                                                        const remark = remarks[`${page.pageId}-${questionIndex}`];
                                                                        if (!remark) {
                                                                            setRemarksErr(true);
                                                                            return;
                                                                        }
                                                                        submissionData.pagesAndQuestions[pageIndex].questions[questionIndex].isApproved = false;
                                                                        submissionData.pagesAndQuestions[pageIndex].questions[questionIndex].isRejected = true;
                                                                        submissionData.pagesAndQuestions[pageIndex].questions[questionIndex].isEmpty = false;
                                                                        submissionData.pagesAndQuestions[pageIndex].questions[questionIndex].allocatedPoints = 0;
                                                                        setAllocatedPoints(prev => ({ ...prev, [`${page.pageId}-${questionIndex}`]: 0 }));
                                                                        submissionData.pagesAndQuestions[pageIndex].questions[questionIndex].remarks = remarks[`${page.pageId}-${questionIndex}`];
                                                                        setSubmissionData({ ...submissionData });
                                                                        calculateTotalAllocatedPoints();
                                                                        // Move to the next question or page
                                                                        if (questionIndex < page.questions.length - 1) {
                                                                            setExpandedQuestionIndex(prev => ({ ...prev, [pageIndex]: questionIndex + 1 }));
                                                                        } else if (pageIndex < submissionData.pagesAndQuestions.length - 1) {
                                                                            setSelectedTab(submissionData.pagesAndQuestions[pageIndex + 1].pageId);
                                                                            setExpandedQuestionIndex(prev => ({ ...prev, [pageIndex + 1]: 0 }));
                                                                        }
                                                                    }}
                                                                >
                                                                    Reject
                                                                </button>
                                                                {
                                                                    !challengeData?.pagesAndQuestions[pageIndex]?.questions[questionIndex]?.submissionRequired && (
                                                                        <button
                                                                            className={`flex-1 bg-secondary hover:bg-navBtn text-white hover:text-black font-bold text-base py-2 rounded-md ${disabledControl ? 'opacity-50 cursor-not-allowed' : ''}`}
                                                                            disabled={submissionData.reviewStatus === 'submitted' && disabledControl}
                                                                            // onClick={() => !question.isApproved && handleApprove(question._id, allocatedPoints[`${page.pageId}-${questionIndex}`], remarks[`${page.pageId}-${questionIndex}`])}
                                                                            onClick={() => {
                                                                                // Automatically set points to 0 and remarks to "Not Submitted!"
                                                                                submissionData.pagesAndQuestions[pageIndex].questions[questionIndex].isApproved = false;
                                                                                submissionData.pagesAndQuestions[pageIndex].questions[questionIndex].isRejected = false;
                                                                                submissionData.pagesAndQuestions[pageIndex].questions[questionIndex].isEmpty = true;
                                                                                submissionData.pagesAndQuestions[pageIndex].questions[questionIndex].allocatedPoints = 0;
                                                                                setTimeout(() => {
                                                                                    setAllocatedPoints(prev => ({ ...prev, [`${page.pageId}-${questionIndex}`]: 0 }));
                                                                                }, 50);
                                                                                // Automatically set remarks to "Not Submitted!"
                                                                                const notSubmittedRemark = "Not Submitted!";
                                                                                setRemarks(prev => ({ ...prev, [`${page.pageId}-${questionIndex}`]: notSubmittedRemark }));
                                                                                submissionData.pagesAndQuestions[pageIndex].questions[questionIndex].remarks = notSubmittedRemark;
                                                                                setSubmissionData({ ...submissionData });
                                                                                calculateTotalAllocatedPoints();
                                                                                // Clear any remarks error
                                                                                setRemarksErr('');
                                                                                // Move to the next question or page
                                                                                if (questionIndex < page.questions.length - 1) {
                                                                                    setExpandedQuestionIndex(prev => ({ ...prev, [pageIndex]: questionIndex + 1 }));
                                                                                } else if (pageIndex < submissionData.pagesAndQuestions.length - 1) {
                                                                                    setSelectedTab(submissionData.pagesAndQuestions[pageIndex + 1].pageId);
                                                                                    setExpandedQuestionIndex(prev => ({ ...prev, [pageIndex + 1]: 0 }));
                                                                                }
                                                                            }}
                                                                        >
                                                                            Not Submitted
                                                                        </button>
                                                                    )
                                                                }
                                                                <button
                                                                    className={`flex-1 bg-primary hover:bg-primaryHoverDark text-white font-bold text-base py-2 rounded-md ${disabledControl ? 'opacity-50 cursor-not-allowed' : ''}`}
                                                                    disabled={submissionData.reviewStatus === 'submitted' && disabledControl}
                                                                    // onClick={() => !question.isApproved && handleApprove(question._id, allocatedPoints[`${page.pageId}-${questionIndex}`], remarks[`${page.pageId}-${questionIndex}`])}
                                                                    onClick={() => {
                                                                        // if (question.isApproved) return;
                                                                        submissionData.pagesAndQuestions[pageIndex].questions[questionIndex].isApproved = true;
                                                                        submissionData.pagesAndQuestions[pageIndex].questions[questionIndex].isRejected = false;
                                                                        submissionData.pagesAndQuestions[pageIndex].questions[questionIndex].isEmpty = false;
                                                                        submissionData.pagesAndQuestions[pageIndex].questions[questionIndex].allocatedPoints = allocatedPoints[`${page.pageId}-${questionIndex}`];
                                                                        submissionData.pagesAndQuestions[pageIndex].questions[questionIndex].remarks = remarks[`${page.pageId}-${questionIndex}`];
                                                                        submissionData.allocatedPoints = parseInt(submissionData.allocatedPoints) + allocatedPoints[`${page.pageId}-${questionIndex}`];
                                                                        setSubmissionData({ ...submissionData });
                                                                        setRemarksErr(false);
                                                                        calculateTotalAllocatedPoints();
                                                                        // Move to the next question or page
                                                                        if (questionIndex < page.questions.length - 1) {
                                                                            setExpandedQuestionIndex({ [pageIndex]: questionIndex + 1 });
                                                                        } else if (pageIndex < submissionData.pagesAndQuestions.length - 1) {
                                                                            setSelectedTab(submissionData.pagesAndQuestions[pageIndex + 1].pageId);
                                                                            setExpandedQuestionIndex({ [pageIndex + 1]: 0 });
                                                                        }
                                                                    }}
                                                                >
                                                                    Approve
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    }
                                })}
                            </div>
                        );
                    })
                }
            </div>

            <div className="flex justify-end mt-5">
                <ActionButton
                    variant={"submit"}
                    text={disabledControl ? "Update Challenge Verification" : "Submit Challenge Verification"}
                    handleSubmit={handleSubmit}
                />
            </div>
            <ToastContainer
                position="top-right"
            />
        </div>
    );
}

export default SubmissionDetails;